import * as React from "react";
import { cn } from "@/lib/utils";

type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, "prefix"> & {
  containerClassName?: string;
  /** Element rendered before the input (icon, badge). */
  left?: React.ReactNode;
  /** Element rendered after the input (kbd, button). */
  right?: React.ReactNode;
};

/**
 * Gooey input — the focus ring and trailing droplets merge into the
 * input pill via an SVG goo filter. Inspired by the Aceternity
 * gooey-input-demo.
 *
 * Sized to drop into any topbar; pass containerClassName to widen.
 */
export const GooeyInput = React.forwardRef<HTMLInputElement, Props>(
  ({ className, containerClassName, left, right, onFocus, onBlur, onChange, ...rest }, ref) => {
    const [focused, setFocused]   = React.useState(false);
    const [hasValue, setHasValue] = React.useState(!!rest.defaultValue || !!rest.value);
    const id = React.useId();

    return (
      <div className={cn("relative", containerClassName)}>
        {/* Goo filter — sucks the droplets back into the pill. */}
        <svg className="absolute -top-0 -left-0 w-0 h-0" aria-hidden>
          <defs>
            <filter id={`goo-${id}`}>
              <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="blur" />
              <feColorMatrix in="blur" mode="matrix"
                values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -7"
                result="goo" />
              <feComposite in="SourceGraphic" in2="goo" operator="atop" />
            </filter>
          </defs>
        </svg>

        <div
          className={cn(
            "relative",
            "[filter:url(#goo-" + id + ")]"
          )}
          style={{ filter: `url(#goo-${id})` }}
        >
          {/* Droplets — three little blobs that ride the ring while focused. */}
          {focused && (
            <>
              <span className="pointer-events-none absolute -right-1 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-accent animate-[droplet1_3.2s_ease-in-out_infinite]" />
              <span className="pointer-events-none absolute right-2 -top-1 w-1.5 h-1.5 rounded-full bg-accent animate-[droplet2_3.8s_ease-in-out_infinite]" />
              <span className="pointer-events-none absolute right-4 -bottom-1 w-1.5 h-1.5 rounded-full bg-accent animate-[droplet3_4.6s_ease-in-out_infinite]" />
            </>
          )}

          <div
            className={cn(
              "relative flex items-center gap-2 rounded-full bg-bg-2 border transition-all duration-300",
              focused ? "border-accent shadow-[0_0_0_3px_rgba(201,169,106,0.18)]" : "border-line",
              "h-10 pl-3.5 pr-2"
            )}
          >
            {left && <span className="shrink-0 text-fg-3 [filter:none]">{left}</span>}
            <input
              ref={ref}
              {...rest}
              onFocus={e => { setFocused(true);  onFocus?.(e); }}
              onBlur={e  => { setFocused(false); onBlur?.(e); }}
              onChange={e => { setHasValue(!!e.target.value); onChange?.(e); }}
              className={cn(
                "flex-1 bg-transparent outline-none text-sm placeholder:text-fg-3 [filter:none]",
                className
              )}
            />
            {right && <span className="shrink-0 [filter:none]">{right}</span>}
          </div>
        </div>

        {/* Floating hint underline when focused. */}
        <span
          className={cn(
            "pointer-events-none absolute -bottom-1 left-1/2 -translate-x-1/2 h-px bg-accent transition-all duration-300",
            focused || hasValue ? "w-1/2 opacity-80" : "w-0 opacity-0"
          )}
        />

        <style>{`
          @keyframes droplet1 { 0%,100% { transform: translate(0,-50%) scale(1); } 50% { transform: translate(14px,-50%) scale(0.7); } }
          @keyframes droplet2 { 0%,100% { transform: translate(0,0) scale(1); }      50% { transform: translate(10px,-6px) scale(0.65); } }
          @keyframes droplet3 { 0%,100% { transform: translate(0,0) scale(1); }      50% { transform: translate(-8px,6px) scale(0.6); } }
        `}</style>
      </div>
    );
  }
);
GooeyInput.displayName = "GooeyInput";
